import React, { useEffect, useState } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Button, Card, Alert } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faEdit, faTrash, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import { SchoolClassCountSetting } from "./SchoolClassCountSetting";

const PageContainer = styled.div`
  max-width: 1000px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const InfoCard = styled(Card)`
  margin-bottom: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const InfoItem = styled.div`
  label {
    font-size: 12px;
    color: ${colors.nightlyWoods600};
    text-transform: uppercase;
    display: block;
    margin-bottom: 4px;
  }
  span {
    font-size: 14px;
    color: ${colors.nightlyWoods900};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const SchoolDetail = () => {
  const { id } = useParams();
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [curricula, setCurricula] = useState([]);

  useEffect(() => {
    const loadCurricula = async () => {
      try {
        const data = await request("curriculum", { method: "GET" });
        setCurricula(data || []);
      } catch {
        setCurricula([]);
      }
    };
    loadCurricula();
  }, []);

  const curriculumName = school?.curriculum_id
    ? curricula.find(c => c.id === school.curriculum_id)?.name
    : null;

  useEffect(() => {
    if (userLoading) return;
    if (authUserData && authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }
    if (!hasFetched) {
      setHasFetched(true);
      fetchSchool();
    }
  }, [authUserData, id, userLoading, hasFetched]);

  const fetchSchool = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request(`admin/schools/${id}`, { method: "GET" });
      setSchool(response);
    } catch (err) {
      console.error("Failed to fetch school:", err);
      setError("Failed to load school. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    history.push(generateRoutePath(constants.SCHOOL_ADMIN_EDIT_ROUTE_NAME, { id }));
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this school?")) return;
    try {
      await request(`admin/schools/${id}`, { method: "DELETE" });
      if (school?.client_id) {
        history.push(generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: school.client_id }));
      } else {
        history.push(generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME));
      }
    } catch (err) {
      console.error("Failed to delete school:", err);
      setError("Failed to delete school. Please try again.");
    }
  };

  const getBackLink = () => {
    if (school?.client_id) {
      return generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id: school.client_id });
    }
    return generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME);
  };

  if (userLoading || loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!school) {
    return <Alert variant="warning">School not found.</Alert>;
  }

  return (
    <PageContainer>
      <BackLink to={getBackLink()}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Client
      </BackLink>

      <PageHeader>
        <PageTitle>{school.name}</PageTitle>
        <ActionButtons>
          <Button variant="outline-secondary" onClick={handleEdit}>
            <FontAwesomeIcon icon={faEdit} /> Edit
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </Button>
        </ActionButtons>
      </PageHeader>

      <InfoCard>
        <Card.Body>
          <InfoGrid>
            <InfoItem>
              <label>Domain</label>
              <span>{school.domain || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Auth Method</label>
              <span>{school.auth_method || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Address</label>
              <span>{school.address || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>City</label>
              <span>{school.city || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>State</label>
              <span>{school.state || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>ZIP Code</label>
              <span>{school.zip_code || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>NCES School ID</label>
              <span>{school.nces_school_id || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>District Name</label>
              <span>{school.district_name || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Additional Allowed Domains</label>
              <span>{school.allowed_domains?.join(", ") || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Standards Framework</label>
              <span>{school.standards_framework_id || "Common Core (CCSS)"}</span>
            </InfoItem>
            <InfoItem>
              <label>Curriculum</label>
              <span>{curriculumName || "-"}</span>
            </InfoItem>
          </InfoGrid>
        </Card.Body>
      </InfoCard>

      <SchoolClassCountSetting schoolId={id} />
    </PageContainer>
  );
};
